import type { TLanguage } from '@semios/app-platform-i18n-core'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import type { WeatherForecastAlertOnly } from '../_types'
import type { AlertsResponse } from './useAlerts'

const getStartDateAndEndDate = (startDate: string | null, endDate: string | null) => {
  const currentYear = new Date().getFullYear()
  const startDateToReturn = startDate ? `${currentYear}-${startDate}` : ''

  let endDateToReturn = ''

  if (startDate && endDate) {
    const endDateToCompare = new Date(`${currentYear}-${endDate}`)
    const startDateToCompare = new Date(`${currentYear}-${startDate}`)

    if (startDateToCompare < endDateToCompare) {
      endDateToReturn = `${currentYear}-${endDate}`
    } else {
      endDateToReturn = `${currentYear + 1}-${endDate}`
    }
  }

  return { startDate: startDateToReturn, endDate: endDateToReturn }
}

export const shapeWeatherForecastAlerts = (response: AlertsResponse): WeatherForecastAlertOnly[] => {
  if (!response) {
    return []
  }

  const { temperature, speed } = userDetailsStore.getState()

  let weatherForecastAlerts: WeatherForecastAlertOnly[] = []

  if (response.legacyWeatherForecastAlerts) {
    weatherForecastAlerts = Object.values(response.legacyWeatherForecastAlerts).map((alert) => {
      const apiRules = alert.alertRules

      let rules: WeatherForecastAlertOnly['rules']

      if ('windGreaterThanOrEqualToKph' in apiRules) {
        rules = {
          windGreaterThanOrEqualToUserUnits: Number(
            unitConverter
              .windSpeed(apiRules.windGreaterThanOrEqualToKph, {
                inputUnit: 'kph',
                outputUnit: speed === 'METRIC' ? 'kph' : 'mph',
              })
              .value(),
          ),
        }
      } else if (
        'includeAllDevices' in apiRules &&
        'measurementHeights' in apiRules &&
        'temperatureLessThanOrEqualToCelsius' in apiRules
      ) {
        rules = {
          includeAllDevices: apiRules.includeAllDevices,
          measurementHeights: apiRules.measurementHeights,
          temperatureLessThanOrEqualToUserUnits: Number(
            unitConverter
              .temperatureInCanopy(apiRules.temperatureLessThanOrEqualToCelsius, {
                inputUnit: 'c',
                outputUnit: temperature === 'METRIC' ? 'c' : 'f',
              })
              .value(),
          ),
        }
      } else if (
        'includeAllDevices' in apiRules &&
        'measurementHeights' in apiRules &&
        'temperatureGreaterThanOrEqualToCelsius' in apiRules
      ) {
        rules = {
          includeAllDevices: apiRules.includeAllDevices,
          measurementHeights: apiRules.measurementHeights,
          temperatureGreaterThanOrEqualToUserUnits: Number(
            unitConverter
              .temperatureInCanopy(apiRules.temperatureGreaterThanOrEqualToCelsius, {
                inputUnit: 'c',
                outputUnit: temperature === 'METRIC' ? 'c' : 'f',
              })
              .value(),
          ),
        }
      } else if ('hasRain' in apiRules) {
        rules = { hasRain: true }
      } else {
        throw new Error('Invalid alert rules')
      }

      const contacts = Object.values(alert.contacts).map((contact) => ({
        id: contact.contactId,
        name: contact.name,
        email: contact.email,
        sms: contact.sms,
        language: contact.language as TLanguage,
        alertsContactMethods: [
          contact.contactViaEmail ? 'email' : null,
          contact.contactViaSMS ? 'sms' : null,
        ].filter((cm): cm is 'email' | 'sms' => Boolean(cm)),
      }))

      const groups: WeatherForecastAlertOnly['groups'] = Object.values(alert.groups).map((group) => ({
        id: group.groupId,
        name: group.name,
        contacts: Object.values(group.contactsForGroup).map((contact) => ({
          id: contact.contactId,
          name: contact.name,
          email: contact.email,
          sms: contact.sms,
        })),
        alertsContactMethods: [
          group.contactViaEmail ? 'email' : null,
          group.contactViaSMS ? 'sms' : null,
        ].filter((cm): cm is 'email' | 'sms' => Boolean(cm)),
      }))

      const { startDate, endDate } = getStartDateAndEndDate(alert.startDate, alert.endDate)

      return {
        id: String(alert.alertId),
        name: alert.name,
        type: {
          id: 'weatherForecastAlert',
        },
        hourOfDay: alert.hourOfDay,
        rules,
        properties: alert.propertyIds?.map((id) => ({ id })) ?? [],
        blocks: alert.blockIds?.map((id) => ({ id })) ?? [],
        stations: alert.stationGeoms?.map((geom) => ({ geom })) ?? [],
        contacts,
        groups: groups,
        startDate,
        endDate,
        createdAt: alert.createdAt,
        updatedAt: alert.updatedAt,
        notifyOwnerByEmail: alert.notifyOwnerByEmail,
        notifyOwnerBySMS: alert.notifyOwnerBySMS,
        deletedAt: alert.deletedAt,
        frequencyMinutes: 1440,
      }
    })
  }

  return weatherForecastAlerts
}
